import { Component, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { IAllergyDetailed } from 'src/app/interfaces/Detailed/IAllergyDetailed';
import { IGetList } from 'src/app/interfaces/Get/IGetList';
import { IWorkspacePatient } from 'src/app/interfaces/Workspace/IWorkspacePatient';
import { PatientService } from 'src/app/services/patient.service';
import { WorkspaceService } from 'src/app/services/workspace.service';
import { AllergiesExpandComponent } from '../../patients/components/alerts-section/allergies/allergies-expand/allergies-expand.component';
import { IWorkspacePatientSearch } from 'src/app/interfaces/Workspace/IWorkspacePatientSearch';
import { IQuickInfo } from 'src/app/interfaces/Workspace/IQuickInfo';
import moment from 'moment';
import { IMedicationDetailed } from 'src/app/interfaces/Medication/IMedicationDetailed';
import { IProblemsDetailed } from 'src/app/interfaces/Problems/IProblemsDetailed';
import { date_format, datetime_format } from 'src/date/date.configuration';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  catchError,
  combineLatest,
  switchMap,
  takeUntil,
} from 'rxjs';
import { LocaleService } from 'src/app/services/locale.service';

@Component({
  selector: 'app-my-patients',
  templateUrl: './my-patients.component.html',
  styleUrls: ['./my-patients.component.css'],
  standalone: false,
})
export class MyPatientsComponent {
  private readonly destroy$ = new Subject<void>();
  private readonly selectedPatientId$ = new BehaviorSubject<string | undefined>(
    undefined,
  );
  // @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  public selectedPatientID?: string;

  public allergiesExpandComp = AllergiesExpandComponent;

  public isLoading = false;

  public profilePicture: Record<number, string> = {};

  public data: MatTableDataSource<IWorkspacePatient> =
    new MatTableDataSource<IWorkspacePatient>([]);

  public totalRecords: number = 0;

  public displayedColumns: string[] = ['workspace-profile', 'workspace-age'];

  public allergies: IGetList<IAllergyDetailed> = {};
  public problems: IGetList<IProblemsDetailed> = {};
  public medications: IGetList<IMedicationDetailed> = {};

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly patientService: PatientService,
    private readonly localeService: LocaleService,
  ) {}

  ngOnInit() {
    combineLatest([this.localeService.locale$, this.selectedPatientId$])
      .pipe(
        switchMap(([, patientId]) => {
          if (!patientId) {
            return EMPTY;
          }

          this.isLoading = true;
          return this.workspaceService.getQuickInfo(patientId).pipe(
            catchError(() => {
              this.isLoading = false;
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.applyQuickInfo(response.data);
        this.isLoading = false;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearPatientsForm() {
    this.data = new MatTableDataSource<IWorkspacePatient>([]);
    this.data.paginator = this.paginator;
  }

  async pageChange(input?: string) {
    const patientObj: IWorkspacePatientSearch = {};
    if (input) patientObj.searchField = input;

    await this.workspaceService
      .getPatients(
        patientObj,
        this.paginator.pageSize,
        this.paginator.pageIndex + 1
      )
      .subscribe({
        next: (d) => {
          this.data = new MatTableDataSource(
            d?.data?.map((d: IWorkspacePatient) => d)
          );

          this.totalRecords = d.totalRecords || 0;

          d?.data.map((d) => {
            if (d.picturePath) {
              this.patientService
                .getProfilePicture(d.id?.toString()!)
                .subscribe({
                  next: (blob?: Blob) => {
                    if (blob) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        this.profilePicture[d.id!] =
                          reader.result?.toString() || '';
                        return reader.result;
                      };
                      reader.readAsDataURL(blob);
                    }
                  },
                  error: (error) => {
                    console.error('Error fetching profile picture:', error);
                    return '';
                  },
                });
            }
          });

          if (d.totalRecords == 1) {
            this.onPatientClick(d.data[0].id?.toString()!);
          }

          if (d.totalRecords == 0) {
            this.selectedPatientID = undefined;
            this.selectedPatientId$.next(undefined);
          }
        },
        error: (err) => {},
      });
  }

  async searchPatientsForm() {
    this.paginator.pageIndex = 0;

    this.pageChange();
  }

  getCurrentDate() {
    return new Date().toISOString().split('T')[0];
  }

  // TO REMOVE LATER
  ngAfterViewInit() {
    this.data.paginator = this.paginator;
    this.searchPatientsForm();
  }

  downloadPicture(id: number): string | void {
    this.patientService.getProfilePicture(id?.toString()!).subscribe({
      next: (blob?: Blob) => {
        if (blob) {
          const reader = new FileReader();
          reader.onload = () => {
            return reader.result;
          };
          reader.readAsDataURL(blob);
        }
      },
      error: (error) => {
        console.error('Error fetching profile picture:', error);
        return '';
      },
    });
  }

  onPatientClick(id: string) {
    this.selectedPatientID = id?.toString();
    this.selectedPatientId$.next(this.selectedPatientID);
  }

  private applyQuickInfo(data?: IQuickInfo) {
    this.allergies = data?.getAllAllergies ?? {};
    this.problems = data?.getAllProblems ?? {};

    this.medications = data?.getAllMedications ?? {};
    this.medications.getAll =
      data?.getAllMedications?.getAll?.map((m: IMedicationDetailed) => ({
        ...m,
        onSetDate: moment(m.onSetDateTime).format(date_format),
        endDate: moment(m.endDateTime).format(date_format),
      })) || [];
  }

  getTableSize() {
    if (window.innerWidth <= 1279) {
      return [5];
    } else {
      return [10];
    }
  }

  getDateTime(value?: string) {
    return value ? moment(value).format(datetime_format) : '-';
  }

  getMedicationValues(): string[][] {
    return (
      this.medications.getAll?.map((medication) => [
        (medication.onSetDateTime
          ? this.getDateTime(medication.onSetDateTime)
          : '') +
          '\n to \n' +
          (medication.endDateTime
            ? this.getDateTime(medication.endDateTime)
            : ''),
        medication.productCombination || '',
        medication.packageCombination || '',
        medication.unitCombination || '',
        medication.frequencyCombination || '',
        medication.durationTreatmentCombination || '',
      ]) || []
    );
  }

  formatDate(date: string) {
    return moment(date).format(date_format);
  }
}
